import { ExtendedNextLink } from 'components/Basic/ExtendedNextLink/ExtendedNextLink';
import { useUserConsentFormMeta } from 'components/Blocks/UserConsent/formMeta';
import Button from 'components/Forms/Button/Button';
import Checkbox from 'components/Forms/Checkbox/Checkbox';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import { Controller } from 'react-hook-form';

const TEST_IDENTIFIER = 'user-consent-form';

export type UserConsentFormProps = {
    cookiePolicyUrl: string;
    onSaveCookieChoicesCallback: () => void;
    onAcceptAllCallback: () => void;
    onRejectAllCallback: () => void;
};

export const UserConsentForm: FC<UserConsentFormProps> = ({
    cookiePolicyUrl,
    onAcceptAllCallback,
    onRejectAllCallback,
    onSaveCookieChoicesCallback,
}) => {
    const { t } = useTranslation();
    const formMeta = useUserConsentFormMeta();

    return (
        <>
            <h5 className="mb-5 text-center uppercase lg:leading-7">{t('User consent popup title')}</h5>
            <div className="max-h-[calc(100vh-350px)] overflow-y-auto">
                <div className="flex flex-col py-2">
                    <Controller
                        name={formMeta.fields.statistics.name}
                        render={({ field }) => (
                            <Checkbox
                                disabled
                                className="mb-2 !font-bold"
                                fieldRef={field}
                                id={formMeta.fields.statistics.name}
                                label={t('User consent functional title')}
                                name={formMeta.fields.statistics.name}
                            />
                        )}
                    />
                    <p className="ml-7">{t('User consent functional text')}</p>
                </div>
                <div className="flex flex-col py-2">
                    <Controller
                        name={formMeta.fields.preferences.name}
                        render={({ field }) => (
                            <Checkbox
                                className="mb-2 font-bold"
                                fieldRef={field}
                                id={formMeta.fields.preferences.name}
                                label={t('User consent preferences title')}
                                name={formMeta.fields.preferences.name}
                            />
                        )}
                    />
                    <p className="ml-7">{t('User consent preferences text')}</p>
                </div>
                <div className="flex flex-col py-2">
                    <Controller
                        name={formMeta.fields.marketing.name}
                        render={({ field }) => (
                            <Checkbox
                                className="mb-2 font-bold"
                                fieldRef={field}
                                id={formMeta.fields.marketing.name}
                                label={t('User consent marketing title')}
                                name={formMeta.fields.marketing.name}
                            />
                        )}
                    />
                    <p className="ml-7">{t('User consent marketing text')}</p>
                </div>
            </div>
            <div className="mb-3 mt-5 flex flex-col items-center gap-3 text-center md:mb-8 md:gap-7">
                <div className="flex w-full flex-col justify-center gap-3 md:flex-row md:gap-7">
                    <Button
                        className="w-full uppercase shadow-grayShadow md:w-fit"
                        data-testid={TEST_IDENTIFIER + '-save'}
                        size="small"
                        type="button"
                        onClick={onSaveCookieChoicesCallback}
                    >
                        {t('Save choices')}
                    </Button>
                    <Button
                        className="w-full uppercase shadow-grayShadow md:w-fit"
                        data-testid={TEST_IDENTIFIER + '-reject'}
                        size="small"
                        type="button"
                        onClick={onRejectAllCallback}
                    >
                        {t('Reject all')}
                    </Button>
                </div>
                <Button
                    className="w-full uppercase shadow-grayShadow md:w-fit"
                    data-testid={TEST_IDENTIFIER + '-accept-all'}
                    type="button"
                    variant="primary"
                    onClick={onAcceptAllCallback}
                >
                    <strong>{t('Accept all')}</strong>
                </Button>
            </div>
            <p className="mb-0 text-center">
                <Trans
                    i18nKey="User consent more info text"
                    components={{
                        link: (
                            <ExtendedNextLink
                                className="text-primary hover:text-grayDark"
                                href={cookiePolicyUrl}
                                rel="noreferrer"
                                target="_blank"
                            />
                        ),
                    }}
                />
            </p>
        </>
    );
};
